import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { handleHashLink } from "../lib/scrollUtils";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "/" },
    { name: "Services & Industries", href: "/#services" },
    { name: "About", href: "/about" },
    { name: "Process", href: "/#process" },
    { name: "Client Access", href: "https://marapyancpa.securefilepro.com/portal/#/login", external: true },
  ];

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    if (href === "/about") return location.pathname === "/about";
    return false;
  };

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-2xl bg-[#F9F8F6]/85 border-b border-[#EBE6DD]"
          : "bg-transparent"
      }`}
      data-testid="header"
    >
      <div className="px-4 sm:px-6 md:px-12 lg:px-16 max-w-[1600px] mx-auto">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 sm:gap-3 group shrink min-w-0"
            data-testid="logo-link"
          >
            <img
              src="/logo_final.png"
              alt="Marapyan CPA Logo"
              className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 object-contain shrink-0 transition-transform duration-500 group-hover:rotate-3"
            />
            <div className="flex flex-col leading-none min-w-0">
              <span
                className="uppercase text-sm sm:text-lg md:text-2xl leading-none tracking-[0.03em] whitespace-nowrap"
                style={{ fontFamily: "'Cinzel Decorative', 'Cormorant Garamond', serif", fontWeight: 400, color: "#4A1F1A" }}
              >
                MARAPYAN CPA, INC
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav
            className="hidden lg:flex items-center gap-10"
            data-testid="desktop-nav"
          >
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium tracking-wide text-[#1C1917] transition-colors relative group"
                  data-testid={`nav-link-${link.name.toLowerCase().split(" ")[0]}`}
                >
                  {link.name}
                  <span className="absolute -bottom-1 left-0 h-px bg-[#C85A43] transition-all duration-500 w-0 group-hover:w-full" />
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={(e) => {
                    setIsMenuOpen(false);
                    if (link.href.startsWith("/#")) {
                      handleHashLink(e, link.href.replace("/#", ""), location.pathname);
                    }
                  }}
                  className={`text-sm font-medium tracking-wide transition-colors relative group ${
                    isActive(link.href) ? "text-[#C85A43]" : "text-[#1C1917]"
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase().split(" ")[0]}`}
                >
                  {link.name}
                  <span
                    className={`absolute -bottom-1 left-0 h-px bg-[#C85A43] transition-all duration-500 ${
                      isActive(link.href) ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </Link>
              )
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-6">
            <a
              href="tel:+18186051215"
              className="text-sm text-[#1C1917] hover:text-[#C85A43] transition-colors font-medium"
              data-testid="phone-link"
            >
              (818) 605-1215
            </a>
            <Link
              to="/#contact"
              onClick={(e) => handleHashLink(e, "contact", location.pathname)}
              className="group inline-flex items-center gap-2 bg-[#1C1917] hover:bg-[#C85A43] text-white rounded-full pl-5 pr-1.5 py-1.5 text-sm font-medium transition-all duration-500"
              data-testid="header-cta-btn"
            >
              <span>Get Started</span>
              <span className="w-8 h-8 rounded-full bg-[#C85A43] group-hover:bg-white flex items-center justify-center transition-all duration-500">
                <ArrowUpRight className="w-3.5 h-3.5 text-white group-hover:text-[#C85A43] transition-colors duration-500" />
              </span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-[#1C1917]"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden bg-[#F9F8F6] border-t border-[#EBE6DD] overflow-hidden"
            data-testid="mobile-menu"
          >
            <nav className="px-6 py-8 flex flex-col gap-6">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsMenuOpen(false)}
                      className="text-2xl font-heading font-medium transition-colors text-[#1C1917]"
                      data-testid={`mobile-nav-link-${link.name.toLowerCase().split(" ")[0]}`}
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      onClick={(e) => {
                        setIsMenuOpen(false);
                        if (link.href.startsWith("/#")) {
                          handleHashLink(e, link.href.replace("/#", ""), location.pathname, { defer: true });
                        }
                      }}
                      className={`text-2xl font-heading font-medium transition-colors ${
                        isActive(link.href) ? "text-[#C85A43]" : "text-[#1C1917]"
                      }`}
                      data-testid={`mobile-nav-link-${link.name.toLowerCase().split(" ")[0]}`}
                    >
                      {link.name}
                    </Link>
                  )}
                </motion.div>
              ))}
              <div className="pt-6 border-t border-[#EBE6DD] space-y-4">
                <a
                  href="tel:+18186051215"
                  className="block text-sm text-[#1C1917] font-medium"
                >
                  (818) 605-1215
                </a>
                <Link
                  to="/#contact"
                  className="inline-flex items-center gap-2 bg-[#1C1917] text-white rounded-full pl-5 pr-1.5 py-1.5 text-sm font-medium"
                  onClick={(e) => {
                    setIsMenuOpen(false);
                    handleHashLink(e, "contact", location.pathname, { defer: true });
                  }}
                  data-testid="mobile-cta-btn"
                >
                  <span>Get Started</span>
                  <span className="w-8 h-8 rounded-full bg-[#C85A43] flex items-center justify-center">
                    <ArrowUpRight className="w-3.5 h-3.5 text-white" />
                  </span>
                </Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
